import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Body,
  Box,
  Button,
  DrawerBottomSheet,
  DrawerHeaderDecoration,
  Heading,
  Modal,
  SemanticDrawerHeader,
  useDrawer,
  WarningIcon,
} from '@edreamsodigeo/prisma-design-system';
import { withDatameshClick, withLoadPlaceholder } from '@/HOC/withDatamesh';
import { useToggleHandler } from '@/hooks/useToggleHandler';
import { useSimpleModal } from '@/hooks/useSimpleModal';
import { useFlowComponent } from '@/hooks/useFlowComponent';

export function RetryErrorComponent(props) {
  const RetryError = useFlowComponent(ErrorDrawer, ErrorModal);

  return <RetryError {...props} />;
}

RetryErrorComponent.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  retryText: PropTypes.string.isRequired,
  reviewText: PropTypes.string.isRequired,
  onRetry: PropTypes.func.isRequired,
  onReview: PropTypes.func.isRequired,
};

function ErrorDrawer(props) {
  const {
    open,
    onClose,
    title,
    text,
    retryText,
    reviewText,
    onRetry,
    onReview,
  } = props;
  const [openDrawer] = useToggleHandler(open);
  const { state, transitionDuration } = useDrawer({
    isOpen: openDrawer,
    transitionDuration: 300,
  });

  return (
    <DrawerBottomSheet state={state} transitionDuration={transitionDuration}>
      <Box data-testid="error_component">
        <ErrorBanner
          onClose={onClose}
          title={title}
          text={text}
          retryText={retryText}
          reviewText={reviewText}
          onRetry={onRetry}
          onReview={onReview}
        />
      </Box>
    </DrawerBottomSheet>
  );
}

function ErrorModal({
  open,
  onClose,
  title,
  text,
  retryText,
  reviewText,
  onRetry,
  onReview,
}) {
  const { modalProps, isOpen, closeModal, openModal } = useSimpleModal(open);

  useEffect(() => {
    if (open) {
      openModal();
    } else {
      closeModal();
    }
  }, [openModal, closeModal, open]);

  return (
    <Modal {...modalProps} size="medium" open={isOpen}>
      <div data-testid="error_component">
        <ErrorBanner
          onClose={onClose}
          title={title}
          text={text}
          retryText={retryText}
          reviewText={reviewText}
          onRetry={onRetry}
          onReview={onReview}
        />
      </div>
    </Modal>
  );
}

const DatameshLoadTrack = withLoadPlaceholder('error:load');
const ButtonWithRetryTracking = withDatameshClick(Button, 'error:retry');
const ButtonWithReviewTracking = withDatameshClick(Button, 'error:review');

function ErrorBanner({
  onClose,
  title,
  text,
  retryText,
  reviewText,
  onRetry,
  onReview,
}) {
  return (
    <>
      <DatameshLoadTrack />
      <Body textAlign="center">
        <SemanticDrawerHeader type="warning">
          <DrawerHeaderDecoration>
            <WarningIcon size="medium" color="darkgoldenrod" />
          </DrawerHeaderDecoration>
        </SemanticDrawerHeader>
      </Body>
      <Box p="big" pt="3x_big">
        <Heading textAlign="center" type="2" as="h2" weight="medium">
          {title}
        </Heading>
        <Box py="base">
          <Body textAlign="center" type="base" as="p">
            {text}
          </Body>
        </Box>
        <Box py="big">
          <ButtonWithRetryTracking
            type="primary"
            size="medium"
            nativeType="button"
            onClick={() => {
              onClose();
              onRetry();
            }}
            fullWidth
            data-testid="error_component_btn_retry"
          >
            {retryText}
          </ButtonWithRetryTracking>
        </Box>
        <ButtonWithReviewTracking
          type="secondary"
          size="medium"
          nativeType="button"
          onClick={() => {
            onClose();
            onReview();
          }}
          fullWidth
          data-testid="error_component_btn_review"
        >
          {reviewText}
        </ButtonWithReviewTracking>
      </Box>
    </>
  );
}
